import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LOCALE_STORAGE_KEY = 'ibc-locale';
export const CN_PATH_PREFIX = '/cn';

export const LOCALES = {
  EN: 'en',
  ZH_CN: 'zh-CN',
};

const LocaleContext = createContext(null);

/**
 * Returns the path without the locale prefix (e.g. /cn/about -> /about)
 */
export function getPathWithoutLocale(pathname) {
  if (pathname.startsWith(CN_PATH_PREFIX)) {
    const rest = pathname.slice(CN_PATH_PREFIX.length) || '/';
    return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return pathname;
}

/**
 * Returns the path with locale prefix when locale is zh-CN (e.g. /about -> /cn/about)
 */
export function getPathWithLocale(path, locale) {
  if (!path || path === '#') return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (locale === LOCALES.ZH_CN) {
    return normalized === '/' ? CN_PATH_PREFIX : `${CN_PATH_PREFIX}${normalized}`;
  }
  return normalized;
}

export const LocaleProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = location.pathname;
  const localeFromPath = pathname.startsWith(CN_PATH_PREFIX) ? LOCALES.ZH_CN : LOCALES.EN;

  const setLocale = useCallback(
    (newLocale) => {
      if (!Object.values(LOCALES).includes(newLocale)) return;

      const pathWithoutLocale = getPathWithoutLocale(pathname);

      if (newLocale === LOCALES.ZH_CN) {
        const newPath = pathWithoutLocale === '/' ? CN_PATH_PREFIX : `${CN_PATH_PREFIX}${pathWithoutLocale}`;
        navigate(newPath);
      } else {
        navigate(pathWithoutLocale || '/');
      }

      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
      } catch (e) {
        // localStorage not available
      }
    },
    [pathname, navigate]
  );

  useEffect(() => {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, localeFromPath);
    } catch (e) {
      // localStorage not available
    }
  }, [localeFromPath]);

  const value = useMemo(
    () => ({
      locale: localeFromPath,
      setLocale,
      getLocalePath: (path) => getPathWithLocale(path, localeFromPath),
    }),
    [localeFromPath, setLocale]
  );

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

/**
 * Hook to get a path with the current locale prefix applied.
 * Use for all internal navigation links.
 */
export function useLocalePath(path) {
  const { getLocalePath } = useLocale();
  return getLocalePath(path);
}

export default LocaleContext;
